import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Film, X, Loader2, CheckCircle, AlertCircle, Play, Download, Eye, Upload as UploadIcon } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, RegenerateButton, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, inputNeed } from '../../utils/nodeRequirements';

const FrameExtractorNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [returnFirstFrame, setReturnFirstFrame] = useState(data.returnFirstFrame ?? false);

  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const getInputForNode = useWorkflowStore((state) => state.getInputForNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const edges = useWorkflowStore((state) => state.edges);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);

  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const { openImagePreview } = useMediaPreview();

  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';

  const hasInput = edges.some((edge) => edge.target === id);
  const inputData = getInputForNode(id);

  const handleSourcePick = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.url) {
        updateNodeData(id, { galleryUrl: result.url, galleryFileName: result.fileName, galleryFile: null });
      } else if (result.file) {
        // Store the actual File object to avoid base64 conversion issues with large videos
        updateNodeData(id, { galleryUrl: URL.createObjectURL(result.file), galleryFileName: result.file.name, galleryFile: result.file });
      }
    });
    openSourcePicker(id, ['video'], 'video/mp4,video/avi,video/mov,video/webm,video/quicktime');
  }, [id, openSourcePicker, updateNodeData]);

  const handleFrameToggle = useCallback((value) => {
    setReturnFirstFrame(value);
    updateNodeData(id, { returnFirstFrame: value });
  }, [id, updateNodeData]);

  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => {
        setChronometer((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProcess = useCallback(async () => {
    const input = getInputForNode(id);
    const effectiveInput = input || (data.galleryUrl ? { outputUrl: data.galleryUrl, fileName: data.galleryFileName, file: data.galleryFile } : null);
    if (!effectiveInput) return false;

    resetExec();

    let videoFile = effectiveInput.file;
    if (!videoFile) {
      const fileUrl = effectiveInput.outputUrl || effectiveInput.preview;
      if (fileUrl) {
        // If already an HTTP URL (R2), pass directly as param — no re-upload needed
        if (fileUrl.startsWith('http')) {
          const result = await processNode({
            resultType: 'frame_extractor',
            params: { video_url: fileUrl, return_first_frame: returnFirstFrame },
          });
          if (result?.outputUrl) {
            updateNodeData(id, {
              hasOutput: true,
              outputUrl: result.outputUrl,
              assetId: result.assetId ?? null,
              fileName: `frame_${effectiveInput.fileName || 'extracted'}.jpg`,
              processedAt: new Date().toISOString(),
            });
            addAsset({
              id: `asset_${Date.now()}`,
              type: 'image',
              tool: 'frameExtractor',
              parent: effectiveInput.fileName,
              filename: `frame_${effectiveInput.fileName || 'extracted'}.jpg`,
              url: result.outputUrl,
              createdAt: new Date().toISOString(),
            });
            return true;
          }
          return false;
        }
        // For data: or blob: URLs with valid object, fetch to create File
        if (!fileUrl.startsWith('blob:')) {
          try {
            const resp = await fetch(fileUrl);
            const blob = await resp.blob();
            videoFile = new File([blob], effectiveInput.fileName || 'input.mp4', { type: blob.type || 'video/mp4' });
          } catch (e) {
            console.error('Error fetching file from URL:', e);
            return false;
          }
        }
      }
    }
    if (!videoFile) return false;

    const result = await processNode({
      resultType: 'frame_extractor',
      files: [{ key: 'video_url', file: videoFile }],
      params: { return_first_frame: returnFirstFrame },
    });

    if (result?.outputUrl) {
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        fileName: `frame_${effectiveInput.fileName || 'extracted'}.jpg`,
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'image',
        tool: 'frameExtractor',
        parent: effectiveInput.fileName,
        filename: `frame_${effectiveInput.fileName || 'extracted'}.jpg`,
        url: result.outputUrl,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, data.galleryUrl, data.galleryFileName, data.galleryFile, returnFirstFrame, getInputForNode, updateNodeData, addAsset, processNode, resetExec]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);
  const handleRegenerate = useCallback(() => {
    setReconfigure(false);
    updateNodeData(id, { hasOutput: false, outputUrl: null });
  }, [id, updateNodeData, setReconfigure]);
  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([inputNeed('video', !!((hasInput && inputData?.hasOutput) || data.galleryUrl), { connected: hasInput })]);

  return (
    <div className={`node-container frameExtractor-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`}>
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />
      {reconfigure && (
        <ReconfigureNotice
          previewUrl={data.thumbnailUrl || null}
          onCancel={() => setReconfigure(false)}
          testId="frameextractor-reconfigure-notice"
        />
      )}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="node-handle input-handle"
      />

      {/* Header */}
      <div className="node-header frameExtractor-header">
        <div className="node-header-left">
          <Film size={16} />
          <span>Frame Extractor</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)} data-testid="delete-frame-extractor">
          <X size={14} />
        </button>
      </div>

      {/* Content */}
      <div className="node-content">
        {/* Input Status */}
        <div className={`node-input-status ${(hasInput && inputData?.hasOutput) || data.galleryUrl ? 'connected' : 'disconnected'}`}>
          {(hasInput && inputData?.hasOutput) || data.galleryUrl ? (
            <>
              <CheckCircle size={12} />
              <span>{data.galleryUrl ? 'Desde galería' : 'Entrada conectada'}</span>
            </>
          ) : (
            <>
              <AlertCircle size={12} />
              <span>Sin entrada</span>
              <button type="button" className="node-load-btn" onClick={handleSourcePick} data-testid="load-btn-frame-extractor">
                <UploadIcon size={12} /> Seleccionar
              </button>
            </>
          )}
        </div>

        {/* Frame Selection Toggle */}
        {!data.outputUrl && !isProcessing && (
          <div className="frameExtractor-toggle" data-testid="frame-toggle">
            <span className="frameExtractor-toggle-label">Extraer:</span>
            <div className="frameExtractor-toggle-buttons">
              <button
                className={`frameExtractor-toggle-btn ${returnFirstFrame ? 'active' : ''}`}
                onClick={() => handleFrameToggle(true)}
                data-testid="frame-toggle-first"
              >
                Primer Frame
              </button>
              <button
                className={`frameExtractor-toggle-btn ${!returnFirstFrame ? 'active' : ''}`}
                onClick={() => handleFrameToggle(false)}
                data-testid="frame-toggle-last"
              >
                Último Frame
              </button>
            </div>
          </div>
        )}

        {/* Processing State */}
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">
              {formatTime(chronometer)}
            </div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>
                {execStatus === 'uploading' ? 'Subiendo video...' :
                 execStatus === 'queued' ? 'En cola...' :
                 'Extrayendo frame...'}
              </span>
            </div>
          </div>
        ) : !showForm ? (
          <div className="node-result">
            <img
              src={data.outputUrl}
              alt="Extracted Frame"
              className="node-result-image clickable"
              onClick={() => openImagePreview(data.outputUrl, `Frame ${data.returnFirstFrame ? 'Primero' : 'Último'}`)}
            />
            <div className="node-result-actions">
              <RegenerateButton onClick={handleRegenerate} testId="frameextractor-regen-btn" />
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="frameextractor-reconfigure-btn" />
              <button
                className="node-mini-preview-btn"
                onClick={() => openImagePreview(data.outputUrl, `Frame ${data.returnFirstFrame ? 'Primero' : 'Último'}`)}
                data-testid="frame-preview-btn"
              >
                <Eye size={12} />
                Ver
              </button>
              <button
                className="node-mini-download-btn"
                onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `frame_${Date.now()}.jpg`); }}
                data-testid="frame-download-btn"
              >
                <Download size={12} />
                Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="image" label="frameextractor" testIdPrefix="frameextractor-timeline" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Frame extraído</span>
            </div>
          </div>
        ) : (
          <div className="node-action-area">
            <p className="node-description">
              Extrae el primer o último frame de un video como imagen
            </p>
            <button
              className="node-process-btn"
              onClick={handleProcess}
              disabled={!((hasInput && inputData?.hasOutput) || data.galleryUrl)}
              title={requirementsTitle(runMissing)}
              data-testid="process-frame-extractor"
            >
              <Play size={14} />
              Extraer Frame
            </button>
            <NodeRequirements missing={runMissing} />
          </div>
        )}

        {/* Error Display */}
        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

FrameExtractorNode.displayName = 'FrameExtractorNode';

export default FrameExtractorNode;
